import React from 'react';
import axios from 'axios';
import { NodeProps, useReactFlow } from 'reactflow';
import { useSearchParams } from 'react-router-dom';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { AuthenticateSelect } from '../../../../auth/select';
import { TextInput } from '../../../../form/textInput';
import { Select } from '../../../../form/select';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';
import { updateNodeValue } from '../../../../../services/method';
import { operationType, selectDataType } from '../../../../../services/types';
import { webURL } from '../../../../../services/API';
import { Main, Styled } from '../../tools/teams/style';

type Props = NodeProps<operationType> & { setChildMinimize?: any; setChildSettings?: any };

const fileEventOptions = ['created', 'updated', 'renamed', 'moved', 'deleted'];
const listEventOptions = ['created', 'updated', 'deleted'];

const eventModeOptions: selectDataType[] = [
  { id: 'file', name: 'File', refId: 'file', isActive: true },
  { id: 'list_item', name: 'List Item', refId: 'list_item' },
  { id: 'file_or_list_item', name: 'File or List Item', refId: 'file_or_list_item' },
];

function toSelectItems(items: any[] = [], activeId?: string) {
  return items.map((item, index) => ({
    id: item.id,
    refId: item.id,
    name: item.displayName || item.name || item.id || `Item ${index + 1}`,
    isActive: activeId ? item.id === activeId : index === 0,
  }));
}

function getActiveId(items?: any[]) {
  return Array.isArray(items) ? (items.find(item => item.isActive)?.id || items[0]?.id || '') : '';
}

function getCollectionItems(response: any) {
  const data = response?.data?.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.value)) return data.value;
  return [];
}

function normalizeFolderPath(path: string | undefined) {
  return String(path || '').trim();
}

function formatFolderPathLabel(path: string | undefined, fallbackName?: string) {
  const normalized = normalizeFolderPath(path);
  if (!normalized) return 'Drive root';
  if (fallbackName) return fallbackName;

  const rootMatch = normalized.match(/root:(.*)$/i);
  if (rootMatch?.[1]) return rootMatch[1] || 'Drive root';

  const segments = normalized.split('/').filter(Boolean);
  return segments[segments.length - 1] || normalized;
}

function buildFolderIdOptions(folders: any[], selectedId: string | undefined) {
  const currentId = String(selectedId || '').trim();
  const options: selectDataType[] = [
    { id: '', refId: 'empty', name: 'leave blank for drive root', isActive: !currentId },
    ...folders.map((folder: any) => ({
      id: folder.id,
      refId: folder.id,
      name: folder.name || folder.id,
      isActive: currentId ? folder.id === currentId : false,
    })),
  ];

  if (currentId && !folders.some((folder: any) => folder.id === currentId)) {
    options.push({ id: currentId, refId: currentId, name: currentId, isActive: true });
  }

  return options;
}

function buildFolderPathOptions(folders: any[], selectedPath: string | undefined) {
  const currentPath = normalizeFolderPath(selectedPath);
  const options: selectDataType[] = [
    { id: '', refId: 'empty', name: 'Drive root', isActive: !currentPath },
    ...folders
      .map((folder: any) => {
        const path = normalizeFolderPath(folder.path);
        if (!path) return null;
        return {
          id: path,
          refId: folder.id || path,
          name: formatFolderPathLabel(path, folder.name),
          isActive: currentPath ? path === currentPath : false,
        };
      })
      .filter(Boolean) as selectDataType[],
  ];

  if (currentPath && !folders.some((folder: any) => normalizeFolderPath(folder.path) === currentPath)) {
    options.push({ id: currentPath, refId: currentPath, name: formatFolderPathLabel(currentPath), isActive: true });
  }

  return options;
}

export function SharePointTrigger({ id, data, setChildMinimize, setChildSettings }: Props) {
  const { setNodes } = useReactFlow();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');
  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [expanded, setExpanded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [capabilities, setCapabilities] = React.useState<any>(null);

  const [isCopy, setIsCopy] = React.useState(false);
  const [showNotificationUrl, setShowNotificationUrl] = React.useState(false);
  const [showHelpSection, setShowHelpSection] = React.useState(false);
  const [folderOptions, setFolderOptions] = React.useState<any[]>([]);
  const [folderOptionsLoading, setFolderOptionsLoading] = React.useState(false);
  const folderLoadKeyRef = React.useRef('');

  const connectionId = data.value?.connectionId || data.value?.connection?.id || data.value?.connection?._id || '';
  const eventMode = data.value?.eventMode || 'file';
  const driveId = data.value?.driveId || '';
  const fileEvents = data.value?.fileEvents || data.value?.events || fileEventOptions;
  const listEvents = data.value?.listEvents || listEventOptions;

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => setExpanded((value: boolean) => !value));
    if (setChildSettings) setChildSettings(() => setSettings(value => ({ ...value, isToggle: !value.isToggle })));
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (!data.value) {
      updateNodeValue(id, setNodes, {
        connection: null,
        connectionId: null,
        eventMode: 'file',
        fileEvents: fileEventOptions,
        listEvents: listEventOptions,
        status: 'disabled',
        fileStatus: 'disabled',
        sites: [],
        drives: [],
        lists: []
      });
    }
  }, [data.value, id, setNodes]);

  React.useEffect(() => {
    if (!expanded) {
      setShowNotificationUrl(false);
      setShowHelpSection(false);
      setFolderOptions([]);
      folderLoadKeyRef.current = '';
    }
  }, [expanded]);

  React.useEffect(() => {
    if (connectionId) {
      axios.get(`${webURL}/microsoft-files/connections/${connectionId}/capabilities`)
        .then(response => setCapabilities(response.data?.capabilities))
        .catch(() => setCapabilities(null));
    }
  }, [connectionId]);

  const update = (values: any) => updateNodeValue(id, setNodes, values);
  const change = (event: any, key?: string) => key && update({ [key]: typeof event === 'string' ? event : event.target.value });

  const auth = (type: 'add' | 'remove', connection?: any) => {
    if (type === 'add') {
      update({ connection, connectionId: connection?.id || connection?._id || null });
    } else {
      update({ connection: null, connectionId: null, status: 'disabled', fileStatus: 'disabled' });
    }
  };

  const toggleFileEvent = (eventName: string) => update({ fileEvents: fileEvents.includes(eventName) ? fileEvents.filter((value: string) => value !== eventName) : [...fileEvents, eventName] });
  const toggleListEvent = (eventName: string) => update({ listEvents: listEvents.includes(eventName) ? listEvents.filter((value: string) => value !== eventName) : [...listEvents, eventName] });

  const handleSelectEventMode = (selectId: any) => {
    update({ eventMode: selectId });
  };

  const handleSelectCollection = (key: 'sites' | 'drives' | 'lists', selectId: any) => {
    const next = (data.value?.[key] || []).map((item: any) => ({ ...item, isActive: item.id === selectId }));
    const selected = next.find((item: any) => item.isActive);
    const updates: any = { [key]: next };
    if (key === 'sites') {
      updates.siteId = selected?.id || '';
      updates.driveId = '';
      updates.listId = '';
      updates.rootItemId = '';
      updates.rootPath = '';
      updates.drives = [];
      updates.lists = [];
      setFolderOptions([]);
      folderLoadKeyRef.current = '';
    }
    if (key === 'drives') {
      updates.driveId = selected?.id || '';
      updates.rootItemId = '';
      updates.rootPath = '';
      setFolderOptions([]);
      folderLoadKeyRef.current = '';
    }
    if (key === 'lists') updates.listId = selected?.id || '';
    update(updates);
  };

  const loadSites = async () => {
    if (!connectionId) return;
    setLoading(true); setMessage('');
    try {
      const response = await axios.post(`${webURL}/microsoft-files/actions/sharepoint`, { connectionId, operation: 'search_sites', query: '*' });
      const sites = toSelectItems(getCollectionItems(response), data.value?.siteId);
      update({ sites, siteId: getActiveId(sites), driveId: '', listId: '', drives: [], lists: [] });
    } catch (error: any) { setMessage(error?.response?.data?.message || error.message); }
    finally { setLoading(false); }
  };

  const validateSite = async () => {
    if (!connectionId || !data.value?.siteUrl) return;
    setLoading(true); setMessage('');
    try {
      const response = await axios.post(`${webURL}/microsoft-files/actions/sharepoint`, { connectionId, operation: 'get_site', siteUrl: data.value.siteUrl });
      const site = response.data?.data;
      if (!site?.id) throw new Error(response.data?.errors?.[0]?.message || 'Site validation failed');
      const siteItem = { id: site.id, refId: site.id, name: site.displayName || site.name || site.id, isActive: true };
      update({ sites: [siteItem], siteId: site.id, validatedSiteName: siteItem.name, driveId: '', listId: '', drives: [], lists: [] });
      setMessage(`Validated ${siteItem.name}`);
    } catch (error: any) { setMessage(error?.response?.data?.message || error.message); }
    finally { setLoading(false); }
  };

  const loadLibraries = async () => {
    if (!connectionId) return;
    const siteId = data.value?.siteId || getActiveId(data.value?.sites);
    if (!siteId) return;
    setLoading(true); setMessage('');
    try {
      const response = await axios.post(`${webURL}/microsoft-files/actions/sharepoint`, { connectionId, operation: 'list_libraries', siteId });
      const drives = toSelectItems(getCollectionItems(response), data.value?.driveId);
      update({ drives, driveId: getActiveId(drives) });
    } catch (error: any) { setMessage(error?.response?.data?.message || error.message); }
    finally { setLoading(false); }
  };

  const loadLists = async () => {
    if (!connectionId) return;
    const siteId = data.value?.siteId || getActiveId(data.value?.sites);
    if (!siteId) return;
    setLoading(true); setMessage('');
    try {
      const response = await axios.post(`${webURL}/microsoft-files/actions/sharepoint`, { connectionId, operation: 'list_lists', siteId });
      const lists = toSelectItems(getCollectionItems(response), data.value?.listId);
      update({ lists, listId: getActiveId(lists) });
    } catch (error: any) { setMessage(error?.response?.data?.message || error.message); }
    finally { setLoading(false); }
  };

  const loadFolders = async (nextDriveId?: string) => {
    const selectedDriveId = String(nextDriveId || driveId || '').trim();
    if (!connectionId || !selectedDriveId) return;
    setFolderOptionsLoading(true);
    try {
      const response = await axios.post(`${webURL}/microsoft-files/actions/sharepoint`, {
        connectionId,
        operation: 'list_children',
        driveId: selectedDriveId,
        itemId: '',
      });
      const items = getCollectionItems(response);
      const folders = items.filter((item: any) => item?.itemType === 'folder' || item?.folder);
      setFolderOptions(folders);
    } catch (error: any) {
      setFolderOptions([]);
      setMessage(error?.response?.data?.message || error.message || 'Unable to load folders');
    } finally {
      setFolderOptionsLoading(false);
    }
  };

  React.useEffect(() => {
    if (!expanded || !connectionId || !driveId) {
      if (!driveId) {
        setFolderOptions([]);
        folderLoadKeyRef.current = '';
      }
      return;
    }

    const nextKey = `${connectionId}:${driveId}`;
    if (folderLoadKeyRef.current === nextKey) return;
    folderLoadKeyRef.current = nextKey;
    loadFolders(driveId);
  }, [connectionId, driveId, expanded]);

  const enable = async () => {
    if (!projectId || !connectionId) return;
    setLoading(true); setMessage('');
    try {
      const response = await axios.put(`${webURL}/microsoft-files/triggers/${projectId}/${id}`, {
        connectionId,
        eventMode,
        siteId: data.value?.siteId,
        driveId: data.value?.driveId,
        listId: data.value?.listId,
        rootItemId: data.value?.rootItemId,
        rootPath: data.value?.rootPath,
        fileEvents,
        listEvents
      });
      const trigger = response.data?.trigger;
      update({
        status: trigger?.status || 'disabled',
        fileStatus: trigger?.fileStatus || 'disabled',
        subscriptionId: trigger?.subscriptionId,
        expirationDateTime: trigger?.expirationDateTime,
        fileSubscriptionId: trigger?.fileSubscriptionId,
        fileExpirationDateTime: trigger?.fileExpirationDateTime
      });
      // setMessage('Trigger enabled. Existing items were baselined without events.');
    } catch (error: any) { setMessage(error?.response?.data?.message || error.message); update({ status: 'error', fileStatus: 'error' }); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    if (!projectId) return;
    setLoading(true); setMessage('');
    try {
      await axios.delete(`${webURL}/microsoft-files/triggers/${projectId}/${id}`);
      update({
        status: 'disabled',
        fileStatus: 'disabled',
        subscriptionId: '',
        expirationDateTime: '',
        fileSubscriptionId: '',
        fileExpirationDateTime: ''
      });
      setMessage('Trigger disabled.');
    } catch (error: any) { setMessage(error?.response?.data?.message || error.message); }
    finally { setLoading(false); }
  };

  const checkNow = async () => {
    if (!projectId) return;
    setLoading(true); setMessage('');
    try {
      const response = await axios.post(`${webURL}/microsoft-files/triggers/${projectId}/${id}/check`);
      setMessage(`Check completed: ${response.data?.result?.events || 0} event(s).`);
    } catch (error: any) { setMessage(error?.response?.data?.message || error.message); }
    finally { setLoading(false); }
  };

  const needsFile = eventMode === 'file' || eventMode === 'file_or_list_item';
  const needsList = eventMode === 'list_item' || eventMode === 'file_or_list_item';

  const fileConnected = !needsFile || (data.value?.fileSubscriptionId && data.value?.fileStatus === 'active');
  const listConnected = !needsList || (data.value?.subscriptionId && data.value?.status === 'active');
  const isConnected = fileConnected && listConnected && (needsFile || needsList);

  const fileError = data.value?.fileStatus === 'error';
  const listError = data.value?.status === 'error';
  const isError = fileError || listError;

  const warning = !connectionId || (!data.value?.driveId && needsFile) || (!data.value?.listId && needsList) || isError;

  const currentEventModeItems = eventModeOptions.map(option => ({ ...option, isActive: option.id === eventMode }));

  const enableDisabled = loading || !connectionId || (needsFile && !data.value?.driveId) || (needsList && !data.value?.listId) || (needsFile && !fileEvents.length) || (needsList && !listEvents.length);
  const disableDisabled = loading || (!data.value?.subscriptionId && !data.value?.fileSubscriptionId);

  // Link generation for SharePoint doesn't explicitly return webhookUrl via generate-link usually, it's just the generic webhook
  const notificationLink = `${webURL}/microsoft-files/webhook`;
  const statusText = isConnected ? 'Connected' : isError ? 'Error' : data.value?.status || 'Disabled';
  const eventCardStyle: React.CSSProperties = {
    border: '1px solid rgb(220, 220, 220)',
    borderRadius: 12,
    padding: '12px 14px',
    background: '#fafafa',
    marginBottom: 12,
  };
  const eventGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 8,
    marginTop: 10,
  };
  const eventChip = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 10,
    border: active ? '1px solid #0f766e' : '1px solid rgb(215, 215, 215)',
    background: active ? 'rgba(15, 118, 110, 0.08)' : '#fff',
    cursor: 'pointer',
    fontSize: 14,
    color: '#374151',
  });
  const pathPreviewStyle: React.CSSProperties = {
    marginTop: '-2px',
    marginBottom: '12px',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid rgb(220, 220, 220)',
    background: 'linear-gradient(180deg, #fcfcfd 0%, #f7f9fb 100%)',
  };
  const normalizedRootPath = normalizeFolderPath(data.value?.rootPath);

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {warning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : isConnected ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
          </>
        ) : null}

        {expanded ? (
          <ExpandedBlock data={{ id, ...data }} handleClose={() => setExpanded(false)} isWarning={warning} style={{ minWidth: '420px', maxWidth: '500px' }}>
            <Styled style={{ maxWidth: '500px' }}>
              <AuthenticateSelect type="microsoft" defaultId={connectionId} defaultConnection={data.value?.connection} handleAuthenticate={auth} background={data.background} />

              <div className="action">
                <button className="btn1 withBorder" onClick={loadSites} disabled={!connectionId || loading}>Load sites</button>
                <button className="btn1 withBorder" onClick={loadLibraries} disabled={!connectionId || loading || !data.value?.siteId}>Load libraries</button>
                <button className="btn1 withBorder" onClick={loadLists} disabled={!connectionId || loading || !data.value?.siteId}>Load lists</button>
              </div>

              <Select title="Event" data={currentEventModeItems} handleSelectItem={handleSelectEventMode} isSmall />

              {capabilities?.siteInputMode === 'manual' ? (
                <>
                  <TextInput keyRef="siteUrl" value={data.value?.siteUrl} title="Site URL" placeholder="https://tenant.sharepoint.com/sites/site" handleChange={change} />
                  <div className="action">
                    <button type="button" className="btn1 withBorder" disabled={loading || !data.value?.siteUrl} onClick={validateSite}>Validate site</button>
                  </div>
                </>
              ) : null}

              <Select title="Site" data={data.value?.sites || []} handleSelectItem={(itemId) => handleSelectCollection('sites', itemId)} isSmall emptyMsg="Load sites" />

              {needsFile ? (
                <>
                  <Select title="Library" data={data.value?.drives || []} handleSelectItem={(itemId) => handleSelectCollection('drives', itemId)} isSmall emptyMsg="Load libraries" />
                  <div className="action">
                    <button className="btn1 withBorder" onClick={() => loadFolders()} disabled={!connectionId || loading || folderOptionsLoading || !driveId}>Load folders</button>
                  </div>
                  <Select
                    title="Folder ID"
                    data={buildFolderIdOptions(folderOptions, data.value?.rootItemId)}
                    handleSelectItem={(selectId) => {
                      const selectedFolder = folderOptions.find((folder: any) => folder.id === selectId);
                      update({
                        rootItemId: selectId || '',
                        rootPath: selectId ? normalizeFolderPath(selectedFolder?.path) : '',
                      });
                    }}
                    isSmall
                    emptyMsg={folderOptionsLoading ? 'Loading folders...' : 'leave blank for drive root'}
                  />
                  <TextInput
                    title="Folder ID"
                    value={data.value?.rootItemId || ''}
                    placeholder="Folder ID"
                    handleChange={(event: any) => update({ rootItemId: event.target.value })}
                    isNormal
                  />
                  <Select
                    title="Folder Path"
                    data={buildFolderPathOptions(folderOptions, data.value?.rootPath)}
                    handleSelectItem={(selectId) => {
                      const selectedFolder = folderOptions.find((folder: any) => normalizeFolderPath(folder.path) === String(selectId || '').trim());
                      update({
                        rootPath: selectId || '',
                        rootItemId: selectId ? String(selectedFolder?.id || '') : '',
                      });
                    }}
                    isSmall
                    emptyMsg={folderOptionsLoading ? 'Loading folders...' : '/drive/root:/Folder'}
                  />
                  <TextInput
                    title="Folder Path"
                    value={data.value?.rootPath || ''}
                    placeholder="Folder Path"
                    handleChange={(event: any) => update({ rootPath: event.target.value })}
                    isNormal
                  />
                  {normalizedRootPath ? (
                    <div style={pathPreviewStyle}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#4b5563', marginBottom: 6 }}>Selected path</div>
                      <div style={{ fontSize: 13, color: '#0f172a', fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace', wordBreak: 'break-all', lineHeight: 1.45 }}>
                        {normalizedRootPath}
                      </div>
                    </div>
                  ) : null}
                  <div style={eventCardStyle}>
                    <h5 style={{ margin: 0, fontSize: 14, color: '#111827' }}>File events</h5>
                    <div style={eventGridStyle}>
                      {fileEventOptions.map(eventName => (
                        <label key={eventName} style={eventChip(fileEvents.includes(eventName))}>
                          <input type="checkbox" checked={fileEvents.includes(eventName)} onChange={() => toggleFileEvent(eventName)} />
                          <span>{eventName.charAt(0).toUpperCase() + eventName.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}

              {needsList ? (
                <>
                  <Select title="List" data={data.value?.lists || []} handleSelectItem={(itemId) => handleSelectCollection('lists', itemId)} isSmall emptyMsg="Load lists" />
                  <div className="instructions-section">
                    <div style={eventCardStyle}>
                      <h5 style={{ margin: 0, fontSize: 14, color: '#111827' }}>List events</h5>
                      <div style={eventGridStyle}>
                        {listEventOptions.map(eventName => (
                          <label key={eventName} style={eventChip(listEvents.includes(eventName))}>
                            <input type="checkbox" checked={listEvents.includes(eventName)} onChange={() => toggleListEvent(eventName)} />
                            <span>{eventName.charAt(0).toUpperCase() + eventName.slice(1)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              ) : null}

              <div className="action">
                <button type="button" className="btn1 withBorder" disabled={enableDisabled} onClick={enable}>Enable subscription</button>
                <button type="button" className="btn1 withBorder" disabled={loading || !isConnected} onClick={checkNow}>Check now</button>
                <button type="button" className="btn1 withBorder danger" disabled={disableDisabled} onClick={disable}>Disable subscription</button>
              </div>

              <div className={`status ${isConnected ? 'connected' : isError ? 'error' : ''}`}
                style={{ fontSize: '15px', fontWeight: '400' }}>
                Status: {statusText}
              </div>
              {message ? <div className="message" style={{ fontSize: '15px', fontWeight: '400' }}>{message}</div> : null}

              {/* <div style={{ marginTop: 8, paddingTop: 12, borderTop: '1px solid rgb(220, 220, 220)' }}>
                <button type="button" className="btn1 withBorder toggle-btn" onClick={() => setShowHelpSection(prev => !prev)}>
                  {showHelpSection ? 'Hide Help & URL' : 'Show Help & URL'}
                </button>
              </div> */}

              {/* {showHelpSection ? (
                <>
                  <div className="webhook-section">
                    <div className="webhook-header">
                      <h4>SharePoint Notification URL</h4>
                      <button type="button" className="btn1 withBorder toggle-btn" onClick={() => setShowNotificationUrl(prev => !prev)}>
                        {showNotificationUrl ? 'Hide URL' : 'Show URL'}
                      </button>
                    </div>
                    {showNotificationUrl ? (
                      <CopyToClipboard text={notificationLink} onCopy={() => { setIsCopy(true); setTimeout(() => setIsCopy(false), 500); }}>
                        <div className="link-container" title={notificationLink}>
                          <div className="link-with-copy">
                            <FontAwesomeIcon icon={isCopy ? faCheck : faClipboard} className={`copy-icon ${isCopy ? 'copied' : ''}`} />
                            <span className="link-text">{notificationLink}</span>
                          </div>
                        </div>
                      </CopyToClipboard>
                    ) : null}
                  </div>

                  <div className="instructions-section">
                    <h5>Setup Instructions:</h5>
                    <ol className="steps-list">
                      <li>
                        <a
                          href="https://learn.microsoft.com/en-us/graph/api/resources/subscription?view=graph-rest-1.0"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          Open Microsoft Graph subscription docs
                          <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                        </a>
                      </li>
                      <li>Select a Microsoft connection, then load and choose the target site.</li>
                      <li>Load the library, list, or both depending on the event mode.</li>
                      <li>Click Enable subscription to activate the trigger.</li>
                      <li>Use Check now to confirm the trigger can recover pending events.</li>
                    </ol>
                  </div>
                </>
              ) : null} */}
            </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}
